import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  companyIdSelector,
  errors,
  getReportDefaultSchemaState,
  Log,
  reportDataAF,
  reportLastSearchCriteriaByReportIdAF,
  reportsTableColumnsOrderStateAF,
  reportsTableColumnsSizingAF,
  reportsTableColumnsSortingAF,
  reportsTableColumnsVisibilityAF,
  setReportColumnsSizingLocalStore,
} from "@ryde/shared";

import { useReportId } from "./use-report-id";

/**
 * Restores the report's default column layout for the "Reset to Default View" button of the Manage
 * Columns drawer (RYDE-7873). The generic `useResetFinalSchema` writes the per-entity table atoms,
 * which the reports grid never reads — reports are driven by the per-report atoms written here.
 *
 * The defaults are recomputed from the cached report dto and the criteria of its last fetch, so the
 * restored layout matches the currently applied grouping and no refetch is needed: column
 * order/visibility/sorting are client side only.
 */
export const useResetReportSchema = () => {
  const { t } = useTranslation();
  const { reportId } = useReportId();
  const companyId = useRecoilValue(companyIdSelector);
  const reportApiDto = useRecoilValue(reportDataAF(reportId));
  const fetchCriteria = useRecoilValue(
    reportLastSearchCriteriaByReportIdAF(reportId),
  );
  const setColumnOrder = useSetRecoilState(
    reportsTableColumnsOrderStateAF(reportId),
  );
  const setColumnVisibility = useSetRecoilState(
    reportsTableColumnsVisibilityAF(reportId),
  );
  const setColumnSorting = useSetRecoilState(
    reportsTableColumnsSortingAF(reportId),
  );
  const setColumnsSizing = useSetRecoilState(
    reportsTableColumnsSizingAF(reportId),
  );

  const resetReportSchema = useEvent((): void => {
    if (!reportApiDto || !fetchCriteria?.id) {
      Log.warnAndToast(t(errors.reports.reportNotFound), { reportId });
      return;
    }

    const { columnsOrder, columnsVisibility, columnsSorting } =
      getReportDefaultSchemaState({ reportApiDto, fetchCriteria });

    Log.debug("Reset the report layout to the default view", columnsOrder);

    setColumnOrder(columnsOrder);
    setColumnVisibility(columnsVisibility);
    setColumnSorting(columnsSorting);
    setColumnsSizing({});

    if (companyId) {
      setReportColumnsSizingLocalStore({
        companyId,
        reportId,
        columnsSizing: {},
      });
    }
  });

  return { resetReportSchema };
};
